import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Building2, CalendarDays, RefreshCw, Scissors, Sparkles, Users } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import { ActionButton, EmptyState, ErrorState } from '../../components/admin/InventoryLayout';
import { ACCENT, Card, CardHeader, SectionTitle, Stat } from '../admin/Dashboard';

const RANGE_OPTIONS = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
];

const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0));
const formatDateOnly = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
};

const SuperAdminDashboard = () => {
  const { getDashboardOverview, getDashboardTrends, getShops } = useSuperadminAuth();
  const [range, setRange] = useState('month');
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [recentShops, setRecentShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async (nextRange = range) => {
    try {
      setLoading(true);
      setError('');

      const [overviewResponse, trendsResponse, shopsResponse] = await Promise.all([
        getDashboardOverview(),
        getDashboardTrends({ range: nextRange }),
        getShops({ page: 1, pageSize: 6 }),
      ]);

      if (!overviewResponse.ok) {
        throw new Error(overviewResponse.error || 'Failed to load overview');
      }
      if (!trendsResponse.ok) {
        throw new Error(trendsResponse.error || 'Failed to load trends');
      }
      if (!shopsResponse.ok) {
        throw new Error(shopsResponse.error || 'Failed to load shops');
      }

      setOverview(overviewResponse.data);
      setTrends(trendsResponse.data);
      setRecentShops(shopsResponse.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load Super Admin dashboard');
    } finally {
      setLoading(false);
    }
  }, [getDashboardOverview, getDashboardTrends, getShops, range]);

  useEffect(() => {
    loadDashboard(range);
  }, [loadDashboard, range]);

  const chartData = useMemo(() => {
    if (!trends?.series?.bookings_created) return [];
    return trends.series.bookings_created.map((item, index) => ({
      bucket: item.bucket,
      bookings_created: Number(item.value || 0),
      bookings_completed: Number(trends.series.bookings_completed?.[index]?.value || 0),
      scans_created: Number(trends.series.scans_created?.[index]?.value || 0),
      ai_credits_used: Number(trends.series.ai_credits_used?.[index]?.value || 0),
    }));
  }, [trends]);

  const stats = useMemo(() => {
    if (!overview) return [];
    return [
      {
        label: 'Shops',
        value: formatNumber(overview.shops?.total),
        hint: `${formatNumber(overview.shops?.by_status?.active)} active`,
        icon: <Building2 className="h-5 w-5" />,
      },
      {
        label: 'Admins',
        value: formatNumber(overview.users?.admins_total),
        hint: `${formatNumber(overview.users?.disabled_total)} disabled users`,
        icon: <Users className="h-5 w-5" />,
      },
      {
        label: 'Barbers',
        value: formatNumber(overview.users?.barbers_total),
        hint: `${formatNumber(overview.users?.shop_admins)} shop admins`,
        icon: <Scissors className="h-5 w-5" />,
      },
      {
        label: 'Clients',
        value: formatNumber(overview.users?.clients_total),
        hint: `${formatNumber(overview.subscriptions?.by_status?.trialing)} trialing subscriptions`,
        icon: <Users className="h-5 w-5" />,
        tone: 'info',
      },
      {
        label: 'Bookings',
        value: formatNumber(overview.bookings?.total),
        hint: `${formatNumber(overview.bookings?.by_status?.completed)} completed`,
        icon: <CalendarDays className="h-5 w-5" />,
      },
      {
        label: 'AI images',
        value: formatNumber(overview.ai?.total_scans),
        hint: `${formatNumber(overview.ai?.credits_used_total)} credits used`,
        icon: <Sparkles className="h-5 w-5" />,
      },
    ];
  }, [overview]);

  return (
    <div className="space-y-6">
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <BarChart3 className="h-6 w-6 mr-2" style={{ color: ACCENT }} />
              Super Admin Dashboard
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              Global platform metrics using the same CRM dashboard shell and component structure.
            </p>
          </div>
          <ActionButton variant="secondary" size="md" onClick={() => loadDashboard()}>
            <span className="inline-flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </span>
          </ActionButton>
        </div>
      </div>

      {error ? <ErrorState error={error} onRetry={() => loadDashboard()} /> : null}

      <div>
        <SectionTitle>Key Metrics</SectionTitle>
        <div className="mt-3 flex flex-nowrap gap-1.5 sm:gap-2 md:gap-2.5 lg:gap-3 xl:gap-4">
          {(loading ? Array.from({ length: 4 }) : stats.slice(0, 4)).map((stat, index) => (
            <div key={stat?.label || `loading-${index}`} className="flex-1 min-w-0">
              <Stat
                label={stat?.label || 'Loading'}
                value={stat?.value || '...'}
                delta=""
                icon={stat?.icon || <BarChart3 />}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionTitle>Performance Overview</SectionTitle>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mt-4">
          <Card className="lg:col-span-2">
            <CardHeader
              title="Platform trends"
              subtitle="Bookings, scans, and AI usage"
              icon={<BarChart3 className="h-4 w-4" />}
              action={
                <div className="flex items-center gap-2">
                  {RANGE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setRange(option.value)}
                      className={`text-xs px-3 py-1.5 rounded-lg border border-black/5 dark:border-white/10 transition-colors font-medium ${
                        range === option.value ? 'text-white' : 'hover:bg-black/5 dark:hover:bg-white/5'
                      }`}
                      style={range === option.value ? { background: ACCENT } : undefined}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              }
            />
            <div className="p-3">
          {chartData.length ? (
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="bookingsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={ACCENT} stopOpacity={0.42} />
                      <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="scansGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} tickFormatter={(value) => String(value).slice(5, 10)} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 18,
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(24,24,27,0.92)',
                      color: '#fff',
                    }}
                  />
                  <Area type="monotone" dataKey="bookings_created" stroke={ACCENT} fill="url(#bookingsGradient)" strokeWidth={2.5} />
                  <Area type="monotone" dataKey="scans_created" stroke="#2563eb" fill="url(#scansGradient)" strokeWidth={2.25} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState
              title="No trend data yet"
              description="Trend buckets will appear here once bookings and scans exist for the selected range."
              icon={<BarChart3 className="h-6 w-6" />}
            />
          )}
            </div>
          </Card>

          <Card className="lg:col-span-1">
            <CardHeader
              title="Recent shops"
              subtitle="Newest directory records"
              icon={<Building2 className="h-4 w-4" />}
            />
            <div className="p-3 space-y-2">
            {recentShops.length ? (
              recentShops.map((shop) => (
                <div key={shop.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors border border-black/5 dark:border-white/10">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${ACCENT}20` }}>
                    <Building2 className="h-4 w-4" style={{ color: ACCENT }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 truncate">{shop.name}</p>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                        {shop.subscription_status || 'unknown'}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">/{shop.slug || 'n/a'}</div>
                    <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">
                      {formatDateOnly(shop.created_at)} • {shop.admins?.length || 0} admin{shop.admins?.length === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState
                title="No shops loaded"
                description="The shops directory will appear here once the API returns data."
                icon={<Building2 className="h-6 w-6" />}
              />
            )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
